import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import ContentPanel from '@payaca/components/contentPanel/ContentPanel';
import EditJobSection from '../editJob/EditJobSection';
import JobLineItemGroupControl from '../jobLineItemGroupControl/JobLineItemGroupControl';
import SelectItemGroupModal from '../selectItemGroupModal/SelectItemGroupModal';
import SelectItemModal from '../selectItemModal/SelectItemModal';

import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';

import {
  getDeal,
  getJobContent as getJobContentFromState,
  getJobLineItemGroup,
} from '@/utils/stateAccessors';

import {
  JobContent,
  JobLineItemBase,
  JobLineItemGroup,
} from '@payaca/types/jobContentTypes';

import { sortByKey } from '@payaca/utilities/sortable';

import { getEditJobBaseLocationPath } from '../../../helpers/jobHelper';

import { useSelector } from '@/api/state';

import projectKeys from '@/api/queries/project/keyFactory';
import { useQueryClient } from '@tanstack/react-query';
import DealMaterialsListDrawer from '../dealMaterialsListDrawer/DealMaterialsListDrawer';
import './JobLineItemGroupsControl.sass';

type Props = {
  jobIsInvoice: boolean;
  jobContentId: number;
};

const JobLineItemGroupsControl: FC<Props> = ({
  jobContentId,
  jobIsInvoice,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [showMaterialsListDrawer, setShowMaterialsListDrawer] = useState(false);

  const [
    showLineItemSearchModalForGroupId,
    setShowLineItemSearchModalForGroupId,
  ] = useState<null | number>();
  const [isCreatingJobLineItem, setIsCreatingJobLineItem] = useState(false);
  const [isAddingExistingJobLineItem, setIsAddingExistingJobLineItem] =
    useState(false);

  const jobContent: JobContent | undefined = useSelector((state) =>
    getJobContentFromState(state, jobContentId)
  );

  const deal = useSelector((state) => {
    if (!jobContent) return;
    return getDeal(state, jobContent.dealId);
  });

  const isCreatingJobLineItemGroup = useSelector((state) => {
    return state.jobContent.isCreatingJobLineItemGroup;
  });

  const isAddingLineItemGroupToJobContent = useSelector((state) => {
    return state.jobContent.isAddingLineItemGroupToJobContent;
  });

  const [showLineItemGroupModal, setShowLineItemGroupModal] =
    useState<boolean>(false);

  const getJobLineItems = useCallback(() => {
    dispatch(
      jobContentActions.requestGetJobLineItemsForJobContent(jobContentId)
    );
  }, [dispatch, jobContentId]);

  const getJobLineItemGroups = useCallback(() => {
    dispatch(
      jobContentActions.requestGetJobLineItemGroupsForJobContent(jobContentId)
    );
  }, [dispatch, jobContentId]);

  const getJobContent = useCallback(() => {
    dispatch(jobContentActions.requestGetJobContent(jobContentId));
  }, [dispatch, jobContentId]);

  const createJobLineItemGroup = useCallback(() => {
    dispatch(
      jobContentActions.requestCreateJobLineItemGroup(
        { jobContentId: jobContentId },
        () => {
          getJobLineItemGroups();
          getJobContent();
        }
      )
    );
  }, [dispatch, getJobContent, getJobLineItemGroups, jobContentId]);

  const addLineItemGroupToJobContent = useCallback(
    (lineItemGroupId: number) => {
      if (!jobContentId) return;
      dispatch(
        jobContentActions.requestAddLineItemGroupToJobContent(
          jobContentId,
          lineItemGroupId,
          () => {
            dispatch(
              jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
                jobContentId
              )
            );
          }
        )
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has unnecessary dependency: getJobLineItems
    [jobContentId, getJobLineItems]
  );

  const canDeleteItemGroup = useMemo(() => {
    if (!jobContent) return false;
    const jobHasLineItems = !!jobContent?.jobLineItemIds.length;
    const jobHasMultipleItemGroups = jobContent?.jobLineItemGroupIds.length > 1;

    return jobHasLineItems || jobHasMultipleItemGroups;
  }, [jobContent]);

  useEffect(() => {
    // if the last remaining group is deleted, we'll add a new empty one
    if (
      jobContent?.jobLineItemGroupIds &&
      jobContent.jobLineItemGroupIds.length === 0
    ) {
      createJobLineItemGroup();
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'createJobLineItemGroup'
  }, [jobContent]);

  const createJobLineItem = useCallback(
    (
      jobLineItemGroupId: number,
      lineItemId: number | null,
      initialData?: Partial<JobLineItemBase>,
      callback?: (jobLineItemId: number) => void
    ) => {
      if (lineItemId) {
        setIsAddingExistingJobLineItem(true);
      } else {
        setIsCreatingJobLineItem(true);
      }

      dispatch(
        jobContentActions.requestCreateJobLineItem(
          {
            ...initialData,
            jobContentId,
            jobLineItemGroupId,
            lineItemId,
          },
          (jobLineItemId: number) => {
            setIsCreatingJobLineItem(false);
            setIsAddingExistingJobLineItem(false);
            dispatch(
              jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
                jobContentId
              )
            );
            callback?.(jobLineItemId);
          }
        )
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    [jobContentId]
  );

  const goToEditJobLineItemPage = (jobLineItemId: number) => {
    history.push(
      `${getEditJobBaseLocationPath(location.pathname)}/items/${jobLineItemId}`
    );
  };

  const createJobLineItemAndGoToEditJobLineItemPage = (
    jobLineItemGroupId: number,
    lineItemId: number | null,
    initialItemData?: Partial<JobLineItemBase>
  ) => {
    if (jobLineItemGroupId) {
      // create job line item
      createJobLineItem(
        jobLineItemGroupId,
        lineItemId,
        initialItemData,
        (jobLineItemId: number) => {
          if (!lineItemId) {
            // go to edit item page if new item not from li
            goToEditJobLineItemPage(jobLineItemId);
          }

          if (deal?.publicId) {
            void queryClient.invalidateQueries({
              queryKey: projectKeys.profitBreakdown(deal.publicId),
            });
          }
        }
      );
    }
  };

  const jobLineItemGroupIds = jobContent?.jobLineItemGroupIds ?? [];

  const jobLineItemGroups = useSelector((state) =>
    jobLineItemGroupIds.map((id) => getJobLineItemGroup(state, id))
  ).filter(Boolean) as Array<JobLineItemGroup>;

  const sortedJobLineItemGroups = jobLineItemGroups.sort(
    sortByKey('positionIndex')
  );

  const jobLineItemGroupElements = useMemo(() => {
    return sortedJobLineItemGroups.map(({ id: jobLineItemGroupId }, index) => (
      <ContentPanel
        key={`job-line-item-group-control-${jobLineItemGroupId}`}
        hasBoxShadow={false}
      >
        <JobLineItemGroupControl
          jobContentId={jobContentId}
          canDeleteItemGroup={canDeleteItemGroup}
          jobIsInvoice={jobIsInvoice}
          jobLineItemGroupId={jobLineItemGroupId}
          handleOpenCreateJobLineItemModal={goToEditJobLineItemPage}
          handleOpenAdvancedSearchLineItemModal={() =>
            setShowLineItemSearchModalForGroupId(jobLineItemGroupId)
          }
          handleCreateJobLineItemAndOpenCreateJobLineItemModal={(
            lineItemId: number | null,
            initialItemData?: Partial<JobLineItemBase>
          ) =>
            createJobLineItemAndGoToEditJobLineItemPage(
              jobLineItemGroupId,
              lineItemId,
              initialItemData
            )
          }
        />
      </ContentPanel>
    ));
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'createJobLineItemAndGoToEditJobLineItemPage', and 'sortedJobLineItemGroups'
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: jobContent.jobLineItemGroupIds
  }, [
    jobContent?.jobLineItemGroupIds,
    jobContentId,
    canDeleteItemGroup,
    jobIsInvoice,
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React hook useMemo depends on `goToEditJobLineItemPage`, which changes every render
    goToEditJobLineItemPage,
  ]);

  return (
    <EditJobSection
      title={
        <div className="title-wrapper">
          <h2>Items</h2>
          {!jobIsInvoice && (
            <Button
              styleVariant={ButtonStyleVariant.ANCHOR}
              onClick={() => setShowMaterialsListDrawer(true)}
            >
              Show Materials required
            </Button>
          )}
        </div>
      }
      description={
        <p>
          Add an existing item or{' '}
          <Button
            styleVariant={ButtonStyleVariant.ANCHOR}
            onClick={() => {
              const jobLineItemGroupId = jobContent?.jobLineItemGroupIds[0];
              if (jobLineItemGroupId && !isCreatingJobLineItem) {
                createJobLineItemAndGoToEditJobLineItemPage(
                  jobLineItemGroupId,
                  null
                );
              }
            }}
            isDisabled={
              !jobContent?.jobLineItemGroupIds[0] || isCreatingJobLineItem
            }
          >
            create a new Item
          </Button>
        </p>
      }
      className="job-line-item-groups-control"
    >
      <div className="job-line-item-group-controls-container">
        {jobLineItemGroupElements}
      </div>
      <div className="create-or-add-existing-container line-item-group">
        <Button
          styleVariant={ButtonStyleVariant.ANCHOR}
          isProcessing={isAddingLineItemGroupToJobContent}
          onClick={() => setShowLineItemGroupModal(true)}
        >
          Search existing groups
        </Button>
        <span>or</span>
        <Button
          styleVariant={ButtonStyleVariant.ANCHOR}
          isProcessing={isCreatingJobLineItemGroup}
          onClick={createJobLineItemGroup}
        >
          Create new group
        </Button>
      </div>
      <SelectItemGroupModal
        isOpen={showLineItemGroupModal}
        onClose={() => setShowLineItemGroupModal(false)}
        onSelectItemGroup={(lineItemGroup: any) => {
          addLineItemGroupToJobContent(lineItemGroup.id);
          setShowLineItemGroupModal(false);
        }}
      />

      {/* search item modal */}
      <SelectItemModal
        isOpen={!!showLineItemSearchModalForGroupId}
        onClose={() => setShowLineItemSearchModalForGroupId(null)}
        onSelectItem={(lineItemId: number) => {
          if (
            !isCreatingJobLineItem &&
            !isAddingExistingJobLineItem &&
            showLineItemSearchModalForGroupId
          ) {
            // close select item modal
            setShowLineItemSearchModalForGroupId(null);
            // create job line item and open edit modal
            createJobLineItemAndGoToEditJobLineItemPage(
              showLineItemSearchModalForGroupId,
              lineItemId
            );
          }
        }}
        disabledItemIds={jobContent?.lineItemIds || []}
      />
      {jobContent?.dealId && (
        <DealMaterialsListDrawer
          isOpen={showMaterialsListDrawer}
          onClose={() => setShowMaterialsListDrawer(false)}
          dealId={jobContent.dealId}
        />
      )}
    </EditJobSection>
  );
};
export default JobLineItemGroupsControl;
